import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { addMinutes, isAfter } from 'date-fns';
import { CAP_TABLE_ASYNC_JOB_TITLES } from 'services/generationJobs';
import { AsyncProcessingJob, AsyncProcessorContextType, CreateAsyncProcessingJob } from './AsyncProcessorContext.type';
import { Logger } from '../../utilities';

export const AsyncProcessorContext = createContext<AsyncProcessorContextType>({
  processingJobs: [],
  addJob: () => {},
  removeJob: () => {},
});

const ASYNC_GC_THRESHOLD_IN_MINUTES = 30;

// Cap-table generation and reprocessing both ingest long PDFs and can
// legitimately run far past the default GC window; reaping the job at 30 min
// would drop the in-progress UI while the job is still processing. Keep its UI
// alive until the backend stuck-job watchdog (6h) is the one to fail it.
// Terminal results still remove the job immediately via onResult, so this only
// affects still-active jobs.
const CAP_TABLE_GC_THRESHOLD_IN_MINUTES = 6 * 60;

const gcThresholdMinutesFor = (job: CreateAsyncProcessingJob): number =>
  (CAP_TABLE_ASYNC_JOB_TITLES.includes(job.details.title)
    ? CAP_TABLE_GC_THRESHOLD_IN_MINUTES
    : ASYNC_GC_THRESHOLD_IN_MINUTES);

export const AsyncProcessorProvider = ({ children }: { children: React.ReactNode }) => {
  const [processingJobs, setProcessingJobs] = useState<AsyncProcessingJob[]>([]);
  const intervalsRef = useRef<Record<string, NodeJS.Timeout>>({});
  // Guards each job against overlapping ticks while a poll's callback chain is still running.
  const inFlightRef = useRef<Record<string, boolean>>({});

  const addJob = useCallback((job: CreateAsyncProcessingJob) => {
    setProcessingJobs(prev => {
      const currentDate = new Date();
      const alreadyHasJobById = prev.find(item => item.id === job.id);
      if (alreadyHasJobById) {
        return prev;
      }

      // right now the cleanup time is enforced but can be configured down the road
      return [
        ...prev,
        { ...job, startDate: currentDate, cleanupDate: addMinutes(currentDate, gcThresholdMinutesFor(job)) },
      ];
    });
  }, []);

  const removeJob = useCallback((id: string) => {
    setProcessingJobs(prev => prev.filter(job => job.id !== id));
    if (intervalsRef.current[id]) {
      clearInterval(intervalsRef.current[id]);
      delete intervalsRef.current[id];
      delete inFlightRef.current[id];
    }
  }, []);

  useEffect(() => {
    processingJobs.forEach(job => {
      if (!intervalsRef.current[job.id]) {
        intervalsRef.current[job.id] = setInterval(async () => {
          if (inFlightRef.current[job.id]) {
            return;
          }
          inFlightRef.current[job.id] = true;
          try {
            const args = job.pollingConfig.pollingArgs || [];
            const result = await job.pollingConfig.pollingFunction(...args);
            await job.onResult(result);

            const isAfterThreshold = isAfter(new Date(), job.cleanupDate);
            if (isAfterThreshold) {
              Logger.debug(
                `${job.id} is being cleaned up as threshold is surpassed(startDate: ${job.startDate} - cleanupDate: ${job.cleanupDate})`
              );
              removeJob(job.id);
            }
          } catch (error) {
            job.onError?.(error as Error);
          } finally {
            delete inFlightRef.current[job.id];
          }
        }, job.pollingConfig.pollingInterval);
      }
    });

    const currentJobIds = new Set(processingJobs.map(job => job.id));
    Object.keys(intervalsRef.current).forEach(id => {
      if (!currentJobIds.has(id)) {
        clearInterval(intervalsRef.current[id]);
        delete intervalsRef.current[id];
        delete inFlightRef.current[id];
      }
    });

    return () => {
      // Read the ref at cleanup time (not capture time) so every live interval
      // is cleared. Delete each key as well: a stale key would block the effect
      // body from recreating the interval for a job that is still present,
      // silently stopping its polling whenever the job list changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const intervals = intervalsRef.current;
      Object.entries(intervals).forEach(([id, handle]) => {
        clearInterval(handle);
        delete intervals[id];
      });
    };
  }, [processingJobs, removeJob]);


  const value = useMemo(
    () => ({
      processingJobs,
      addJob,
      removeJob,
    }),
    [processingJobs, addJob, removeJob]
  );

  return <AsyncProcessorContext.Provider value={value}>{children}</AsyncProcessorContext.Provider>;
};

export const useAsyncProcessor = () => {
  const context = useContext(AsyncProcessorContext);
  if (!context) {
    throw new Error('useAsyncProcessor must be used within an AsyncProcessorProvider');
  }
  return context;
};
